import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Loader2, MessageSquare, RotateCw, Sparkles } from 'lucide-react'
import { api, errorMessage } from '@/lib/api'
import { formatDateTime } from '@/lib/date'
import type { AiInsight } from '@/types'
import Card from '@/components/ui/Card'
import Button from '@/components/ui/Button'
import Markdown from '@/components/ui/Markdown'
import Skeleton from '@/components/ui/Skeleton'
import { useToast } from '@/components/ui/toast'
import ProposalCard from './ProposalCard'
import { VerdictBadge } from './AiBadges'
import { toolLabel } from './toolLabels'
import { aiFeatures } from '@/lib/aiFeatures'

/**
 * An MPG AI order check or morning brief on a console screen. The server's
 * queue writes it (a minute or two: the model reasons before it answers), so
 * while it runs this says so and the query polls. Suggestions under it are the
 * same Apply cards as in the chat; nothing changes until someone presses Apply.
 */
export default function InsightCard({
  title,
  insight,
  loading,
  queryKey,
  runPath,
  runLabel,
  rerunLabel,
  emptyText,
  followUp,
  children,
}: {
  title: ReactNode
  insight: AiInsight | null | undefined
  loading: boolean
  /** The query this card reads, refreshed after a run or an Apply. */
  queryKey: readonly unknown[]
  /** POST here to ask for a new one. */
  runPath: string
  runLabel: string
  rerunLabel: string
  emptyText: ReactNode
  /** Opens the chat with this question typed in. */
  followUp?: string
  children?: ReactNode
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const busy = insight?.status === 'queued' || insight?.status === 'running'

  const run = useMutation({
    mutationFn: () => api.post<{ data: AiInsight }>(runPath).then((r) => r.data.data),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, data)
      queryClient.invalidateQueries({ queryKey: ['ai', 'checks'] })
    },
    onError: (error) => toast.error('MPG AI could not start', errorMessage(error)),
  })

  const refresh = () => queryClient.invalidateQueries({ queryKey })

  return (
    <Card className="min-w-0">
      {/* One row on every width: on a phone the button is icon-only at the top
          right (client 2026-10-27), so it never wraps under the title. */}
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {/* The spinning rainbow rim is .ai-ring in index.css (Printscape's Scape AI look). */}
          <span className="ai-ring inline-flex size-9 shrink-0">
            <span className="flex size-full items-center justify-center rounded-full bg-surface text-primary">
              <Sparkles className="size-4" />
            </span>
          </span>
          <span className="text-base font-medium text-foreground">{title}</span>
          {insight?.kind === 'order_check' && insight.status === 'ready' && <VerdictBadge check={insight} />}
        </div>
        <Button
          variant="outline"
          className="size-10 shrink-0 px-0 sm:h-9 sm:w-auto sm:px-3"
          disabled={busy || run.isPending}
          loading={run.isPending}
          aria-label={insight ? rerunLabel : runLabel}
          title={insight ? rerunLabel : runLabel}
          onClick={() => run.mutate()}
        >
          {!run.isPending && <RotateCw className="size-5 shrink-0 sm:size-4" />}
          <span className="hidden sm:inline">{insight ? rerunLabel : runLabel}</span>
        </Button>
      </div>

      {loading && <Skeleton className="h-20" />}

      {!loading && !insight && <p className="text-sm text-foreground/60">{emptyText}</p>}

      {busy && (
        <div className="flex items-center gap-2 rounded-lg border border-primary/20 bg-primary/5 px-3 py-3 text-sm text-foreground/80">
          <Loader2 className="size-4 shrink-0 animate-spin text-primary" />
          <span>
            MPG AI is {insight?.status === 'queued' ? 'about to start' : 'reading the planner and thinking it through'}. This
            usually takes a minute or two; you can leave this page and come back.
          </span>
        </div>
      )}

      {insight?.status === 'failed' && (
        <div className="flex items-start gap-2 rounded-lg border border-danger/40 bg-danger/5 p-3 text-sm">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-danger" />
          <span>{insight.failure ?? 'MPG AI could not finish.'}</span>
        </div>
      )}

      {insight?.status === 'ready' && (
        <>
          <Markdown>{insight.answer}</Markdown>

          {(insight.kind !== 'brief' || aiFeatures.briefActions) &&
            insight.proposals.map((proposal) => <ProposalCard key={proposal.id} proposal={proposal} onChanged={refresh} />)}

          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-foreground/10 pt-3 text-2xs text-foreground/50">
            <span>
              {insight.finished_at ? formatDateTime(insight.finished_at) : formatDateTime(insight.created_at)}
              {insight.requested_by_name ? `, asked by ${insight.requested_by_name}` : ', scheduled'}
            </span>
            {insight.tools.length > 0 && (
              <span title={[...new Set(insight.tools)].map(toolLabel).join(', ')}>
                Used {insight.tools.length} {insight.tools.length === 1 ? 'source' : 'sources'}
              </span>
            )}
            {followUp && (
              <Link to={`/admin/ai?q=${encodeURIComponent(followUp)}`} className="inline-flex items-center gap-1 font-medium text-primary hover:underline">
                <MessageSquare className="size-3" /> Ask a follow-up
              </Link>
            )}
          </div>
        </>
      )}

      {children}
    </Card>
  )
}
